<template>
  <div class="card base-table-header">
    <!-- Header Section -->
    <b-card-header v-show="!disableHeader">
      <slot name="tableHeader" />
      <BaseTableFilterBar
          v-model="filterValues"
          :filterSettings="filterSettings"
          :oldStyleFilterWrap="oldStyleFilterWrap"
          :injectActions="injectActions"
          :firstSearch="firstSearch"
          :useQueryUrl="useQueryUrl"
          :isRowFilter="isRowFilter"
          :showImport="showImport"
          :showExport="showExport"
          :searchButtonText="searchButtonText"
          :importButtonText="importButtonText"
          :dynamicFieldButtonAdd="dynamicFieldButtonAdd"
          :hideSearch="hideSearch"
          @search="onFilterSearch"
          @exportToCSV="exportToCSV"
          @changed="resetPage"
          @addDynamicField="addDynamicFields"
          @importFile="importFile"
          ref="filterBar"
      >
        <!-- Pass–through scoped slots for filters -->
        <template v-for="(_, slotName) in $scopedSlots" v-slot:[slotName]="props">
          <slot :name="slotName" v-bind="props" />
        </template>
      </BaseTableFilterBar>
      <slot name="additionalHeader" />
    </b-card-header>

    <!-- Table Section -->
    <div :style="computedTableWrapperStyles" :class="tableWrapperClasses" class="table-responsive mb-0 base-table-wrapper">
      <table role="table" :aria-busy="isBusy" class="table b-table table-striped table-hover">
        <BaseTableHeader
            :fields="fields"
            :orderBy="params.orderBy"
            :orderDirection="params.orderDirection"
            @sort="sortCol"
        />
        <BaseTableBody
            :items.sync="displayedItems"
            :flattenedFields="flattenedFields"
            :itemsFormat="itemsFormat"
            :secondsToHMSReader="secondsToHMSReader"
            @cell-click="handleCellClick"
            @update-cell="updateCell"
            @selectedRow="$emit('selectedRow', $event)"
        >
          <!-- Pass–through scoped slots for table body -->
          <template v-for="(_, slotName) in $scopedSlots" v-slot:[slotName]="props">
            <slot :name="slotName" v-bind="props" />
          </template>
        </BaseTableBody>
      </table>
      <div v-if="isLoadingSpinner" class="table-loading-overlay d-flex align-items-center justify-content-center">
        <b-spinner variant="primary" label="Loading" />
      </div>
    </div>

    <!-- Footer Section -->
    <b-card-footer :class="{ 'disabled-overlay': isBusy }">
      <slot name="additionalFooter" />
      <b-row>
        <b-col
            cols="12"
            v-show="!displayedItems.length && !isLoadingSpinner"
            style="font-weight: 500; text-align: center"
        >
          {{ isInitOnlyGetFilter && !hasSearchedData ? 'Please apply filters to search' : 'No data' }}
        </b-col>
        <b-col
            cols="12"
            v-if="showPagination && (displayedItems.length || (paginationData && paginationData.is_simple_paginate))"
        >
          <div :style="{ pointerEvents: isBusy ? 'none' : 'auto', opacity: isBusy ? 0.5 : 1 }">
            <pagination-and-table-info
                :data="paginationData"
                :useQueryUrl="useQueryUrl"
                @change="handleChangePage"
            />
          </div>
        </b-col>
      </b-row>
    </b-card-footer>
  </div>
</template>

<script>
import PaginationAndTableInfo from '@ps_main/custom-components/PaginationAndTableInfo.vue'
import { secondsToHMSReader } from '@/utils/helpers'
import BaseTableFilterBar from '@/main/custom-components/BaseTable/BaseTableFilterBar.vue'
import BaseTableHeader from '@/main/custom-components/BaseTable/BaseTableHeader.vue'
import BaseTableBody from '@/main/custom-components/BaseTable/BaseTableBody.vue'

export default {
  name: 'NewBaseTable',
  components: {
    BaseTableBody,
    BaseTableHeader,
    BaseTableFilterBar,
    PaginationAndTableInfo
  },
  props: {
    fields: { type: Array, required: true },
    itemsFormat: { type: String, default: '' },
    filterSettings: { type: [Array, Object], default: () => ({}) },
    oldStyleFilterWrap: { type: Boolean, default: false },
    isRowFilter: { type: Boolean, default: false },
    injectActions: { type: Object, default: () => ({}) },
    itemPerPage: { type: Number, default: 50 },
    defaultSortBy: { type: String, default: 'id' },
    defaultSortDirection: { type: String, default: 'desc' },
    method: { type: String, default: 'get' },
    url: { type: String, required: true },
    showSpinner: { type: Boolean, default: false },
    disableHeader: { type: Boolean, default: false },
    overflowVisible: { type: Boolean, default: false },
    overflowXScroll: { type: Boolean, default: false },
    firstSearch: { type: Boolean, default: true },
    hideSearch: { type: Boolean, default: false },
    customKeyDataPagination: { type: [String, Boolean], default: false },
    isFrontendPagination: { type: Boolean, default: false },
    useQueryUrl: { type: Boolean, default: true },
    indexDynamicField: { type: [Boolean, Number], default: false },
    dynamicFields: { type: Object, required: false },
    dynamicFieldButtonAdd: { type: String, default: 'Add Another' },
    isInitOnlyGetFilter: { type: Boolean, default: false },
    showImport: { type: Boolean, default: false },
    searchButtonText: { type: String, default: 'Search' },
    importUrl: {type: String, default: ''},
    importButtonText: {type: String, default: 'Import'},
    importSuccessMessage: { type: String, default: '' },
    stickyHeader: { type: Boolean, default: false },
    showExport: { type: Boolean, default: false },
    exportUrl: { type: String, default: '' },
    exportName: { type: String, default: 'export' },
    wrapperOverflowY: { type: String, default: '' },
    useSpinnerLoading: { type: Boolean, default: true },
    showPagination: { type: Boolean, default: true },
    httpClient: { type: [Object, Function], default: null },
    urlFilters: { type: String, default: null },
    useEnhancePagination: { type: Boolean, default: false },
    hideTotalCount: { type: Boolean, default: false },
    chunkedUpload: { type: Boolean, default: false }
  },
  data () {
    return {
      isBusy: false,
      isLoadingSpinner: false,
      abortController: null,
      lastFilterSig: '',
      currentRequestSeq: 0,

      // Pagination and sorting parameters
      params: {
        page: 1,
        orderBy: this.useQueryUrl ? (this.$route.query.orderBy ?? this.defaultSortBy) : this.defaultSortBy,
        itemPerPage: this.itemPerPage,
        orderDirection: this.$route.query.orderDirection ?? this.defaultSortDirection,
        initFilter: 0
      },
      // For backend pagination:
      items: [],
      // For frontend pagination:
      allItems: [],
      responseData: null,
      filterValues: {},
      onlyGetFilterHasApplied: false,
      hasSearchedData: false,
      // Dynamic pagination mode
      dynamicIsFrontendPagination: this.isFrontendPagination
    }
  },
  watch: {
    isFrontendPagination (val) {
      if (!this.useEnhancePagination) {
        this.dynamicIsFrontendPagination = val
      }
    },
    onlyGetFilterHasApplied (value) {
      if (value) {
        this.$emit('onlyGetFilterHasApplied')
      }
    }
  },
  computed: {
    httpClientToUse () {
      return this.httpClient || this.$http
    },
    tableWrapperClasses () {
      return {
        'overflow-visible': this.overflowVisible,
        'overflow-x-scroll': this.overflowXScroll,
        'b-table-sticky-header': this.stickyHeader
      }
    },
    computedTableWrapperStyles () {
      const styles = {}
      if (this.stickyHeader) {
        styles.maxHeight = '80vh'
        styles.overflowY = 'auto'
      }
      if (this.wrapperOverflowY) {
        styles.overflowY = this.wrapperOverflowY
      }
      return styles
    },
    isMultipleHeader () {
      return Array.isArray(this.fields[0])
    },
    flattenedFields () {
      if (this.isMultipleHeader) {
        const addToEnd = []
        const flattened = this.fields.reduce((acc, row) => {
          row.forEach(field => {
            if (field?.addToEnd) {
              addToEnd.push(field)
            } else if (!field.colspan) acc.push(field)
          })
          return acc
        }, [])
        flattened.push(...addToEnd)
        return flattened
      }
      return this.fields
    },
    sortedItems () {
      // Ensure allItems is an array
      const items = Array.isArray(this.allItems) ? this.allItems : []
      // If not using frontend pagination or there are no items, return the items array (or an empty array)
      if (!this.dynamicIsFrontendPagination || !items.length) {
        return items
      }
      // Keep summary/pinned rows (flagged with __rowType, e.g. report totals) at the bottom,
      // unaffected by client-side sorting. No-op for tables without such rows.
      const pinnedRows = items.filter(item => item && item.__rowType)
      const rowsToSort = pinnedRows.length ? items.filter(item => !(item && item.__rowType)) : items
      const { orderBy, orderDirection } = this.params
      const direction = orderDirection === 'asc' ? 1 : -1
      const sortedRows = [...rowsToSort].sort((a, b) => {
        const aVal = a[orderBy]
        const bVal = b[orderBy]
        // ISO date (YYYY-MM-DD) comparison first
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/
        if (isoDateRegex.test(aVal) && isoDateRegex.test(bVal)) {
          return direction * (new Date(aVal) - new Date(bVal))
        }
        const fullDateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/
        if (fullDateTimeRegex.test(aVal) && fullDateTimeRegex.test(bVal)) {
          return direction * (new Date(aVal) - new Date(bVal))
        }
        // Date range comparison (DD.MM.YYYY - DD.MM.YYYY) — used by weekly/monthly views
        const dateRangeRegex = /^(\d{2})\.(\d{2})\.(\d{4})/
        const aMatch = typeof aVal === 'string' && aVal.match(dateRangeRegex)
        const bMatch = typeof bVal === 'string' && bVal.match(dateRangeRegex)
        if (aMatch && bMatch) {
          const aDate = new Date(aMatch[3], aMatch[2] - 1, aMatch[1])
          const bDate = new Date(bMatch[3], bMatch[2] - 1, bMatch[1])
          return direction * (aDate - bDate)
        }
        // Numeric comparison (skip ISO dates)
        if (!isoDateRegex.test(aVal) && !isoDateRegex.test(bVal)
            && !isNaN(parseFloat(aVal)) && !isNaN(parseFloat(bVal))) {
          return direction * (parseFloat(aVal) - parseFloat(bVal))
        }
        // Date comparison:
        if (!isNaN(Date.parse(aVal)) && !isNaN(Date.parse(bVal))) {
          return direction * (new Date(aVal) - new Date(bVal))
        }
        // If aVal or bVal is null/undefined, treat it as an empty string (or handle it as needed)
        // Fallback to string comparison:
        const safeAVal = aVal != null ? aVal.toString() : ''
        const safeBVal = bVal != null ? bVal.toString() : ''
        return direction * safeAVal.localeCompare(safeBVal, undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      })
      return pinnedRows.length ? [...sortedRows, ...pinnedRows] : sortedRows
    },
    displayedItems () {
      if (this.dynamicIsFrontendPagination) {
        if (this.showPagination) {
          const start = (this.params.page - 1) * this.params.itemPerPage
          return this.sortedItems.slice(start, start + this.params.itemPerPage)
        } else {
          return this.sortedItems
        }
      }

      return this.items
    },
    paginationData () {
      if (this.dynamicIsFrontendPagination) {
        const { page, itemPerPage } = this.params
        const total = this.allItems?.length ?? 0
        const from = total === 0 ? 0 : (page - 1) * itemPerPage + 1
        const to = Math.min(page * itemPerPage, total)

        return this.laravelPaginationPages({
          total,
          last_page: Math.ceil(total / itemPerPage) || 1,
          from,
          to,
          current_page: page,
          all_items: this.allItems
        })
      }

      let data = this.responseData
      if (this.customKeyDataPagination && data) {
        data = data[this.customKeyDataPagination]
      }

      if (this.hideTotalCount) {
        return this.laravelPaginationPages({ ...data, total: false, last_page: 1 })
      }

      return this.laravelPaginationPages(data)
    }
  },
  methods: {
    secondsToHMSReader,
    onFilterSearch () {
      const currentSig = JSON.stringify(this.filterValues || {})
      // Always cancel any in-flight request and start fresh, regardless of filter equality
      try { this.abortController && this.abortController.abort() } catch (e) {}
      this.abortController = new AbortController()
      this.lastFilterSig = currentSig
      this.search(false)
    },
    prepareAbortController () {
      try { this.abortController && this.abortController.abort() } catch (e) {}
      this.abortController = new AbortController()
      return this.abortController.signal
    },
    resetPage () {
      this.params.page = 1
    },
    showLoadingSpinner () {
      this.isLoadingSpinner = true
    },
    hideLoadingSpinner () {
      this.isLoadingSpinner = false
    },
    handleCellClick ({ field, row }) {
      if (field.editable) {
        this.$emit('editField', { fieldKey: field.key, row })
      }
      this.$emit(`selectedCol${field.key}`, row[field.key])
    },
    updateCell (cellData) {
      const index = this.dynamicIsFrontendPagination ? (this.params.page - 1) * this.params.itemPerPage + cellData.rowIndex : cellData.rowIndex
      if (this.dynamicIsFrontendPagination) {
        this.allItems[index][cellData.field.key] = cellData.value
      } else {
        this.items[cellData.rowIndex][cellData.field.key] = cellData.value
      }
    },
    sortCol (fieldName) {
      if (this.params.orderBy === fieldName) {
        this.params.orderDirection = this.params.orderDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.params.orderBy = fieldName
        this.params.orderDirection = 'desc'
      }
      if (this.useQueryUrl) this.setParamsOrderToUrl()
      if (!this.dynamicIsFrontendPagination) this.search()
    },
    setParamsOrderToUrl () {
      const queryParams = new URLSearchParams(window.location.search)
      queryParams.set('orderBy', this.params.orderBy)
      queryParams.set('orderDirection', this.params.orderDirection)
      window.history.replaceState({}, '', `${window.location.pathname}?${queryParams.toString()}`)
    },
    generateParamsPaginationByUrl () {
      const queryParams = new URLSearchParams(window.location.search)
      const page = queryParams.get('page')
      if (page) this.params.page = parseInt(page)
    },
    generateParamsOrderByUrl () {
      const queryParams = new URLSearchParams(window.location.search)
      const sortBy = queryParams.get('sortBy')
      if (sortBy) this.params.orderBy = sortBy
      const sortDirection = queryParams.get('sortDirection')
      if (sortDirection) this.params.orderDirection = sortDirection
    },
    search (isSilentSearch = false) {
      if (this.url === '') return
      const hasFileInput = Object.values(this.filterValues).some(val => val instanceof File)
      if (this.useQueryUrl) {
        this.$refs.filterBar?.$refs?.filter?.generateFilterValuesByUrl()
        this.generateParamsPaginationByUrl()
        this.generateParamsOrderByUrl()
      }
      if (hasFileInput) {
        this.searchWithFile()
        return
      }
      if (!isSilentSearch) {
        this.isBusy = true
        if (this.showSpinner) this.showLoading()
        if (this.useSpinnerLoading) this.showLoadingSpinner()
      }
      let getParams = { ...this.params, ...this.filterValues }
      let fetchFilters = false
      let fetchData = true

      if (this.params.initFilter === 0) {
        fetchFilters = true
      }

      if (this.isInitOnlyGetFilter && !this.onlyGetFilterHasApplied) {
        getParams.onlyGetFilter = true

        fetchData = false
      }

      if (this.indexDynamicField) {
        getParams.dynamicFieldCount = this.filterSettings[this.indexDynamicField].length - 1
      }
      if (typeof this.injectActions.modifyParams === 'function') {
        getParams = this.injectActions.modifyParams(getParams)
      }

      const requests = []
      const signal = this.prepareAbortController()

      if (fetchFilters && this.urlFilters) {
        const filterParams = { ...getParams, onlyGetFilter: true }
        const config = { params: filterParams, signal }

        requests.push(this.httpClientToUse.get(this.urlFilters, config).then(res => ({ type: 'filter', res })))
      }

      if (fetchData) {
        const dataParams = { ...getParams }

        if (this.method === 'get') {
          requests.push(this.httpClientToUse.get(this.url, { params: dataParams, signal }).then(res => ({ type: 'data', res })))
        } else {
          requests.push(this.httpClientToUse[this.method](this.url, dataParams, { signal }).then(res => ({ type: 'data', res })))
        }
      } else if (fetchFilters && !this.urlFilters) {
        // When we need filters but no separate urlFilters, use main URL with onlyGetFilter param
        if (this.method === 'get') {
          requests.push(this.httpClientToUse.get(this.url, { params: getParams, signal }).then(res => ({ type: 'data', res })))
        } else {
          requests.push(this.httpClientToUse[this.method](this.url, getParams, { signal }).then(res => ({ type: 'data', res })))
        }
      }

      Promise.all(requests)
        .then(results => {
          let combinedData = {}
          let baseResponse = null

          results.forEach(({ type, res }) => {
            if (!baseResponse) baseResponse = res // Use first response as base for headers/status/etc
            
            const data = res.data
             
            if (type === 'filter') {
              combinedData.filterData = data.filterData || data // Handle wrapper vs direct
              if (data.isAdditionalFilter) combinedData.isAdditionalFilter = data.isAdditionalFilter
            } else {
              combinedData = { ...combinedData, ...data }
            }
          })

          const finalResponse = {
            ...baseResponse,
            data: combinedData
          }
          
          this.handleResponse(finalResponse)
        })
        .catch(this.handleErrors)
    },
    searchWithFile () {
      this.isBusy = true
      if (this.showSpinner) this.showLoading()
      if (this.useSpinnerLoading) this.showLoadingSpinner()
      const formData = new FormData()
      Object.entries(this.filterValues).forEach(([key, value]) => {
        if (value instanceof File) formData.append(key, value)
      })
      const mergedParams = { ...this.params, ...this.filterValues }
      Object.entries(mergedParams).forEach(([key, value]) => formData.append(key, value))
      this.httpClientToUse.post(`${this.url}/file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
        .then(this.handleResponse)
        .catch(this.handleErrors)
    },
    importFile () {
      this.isBusy = true
      if (this.showSpinner) this.showLoading()
      if (this.useSpinnerLoading) this.showLoadingSpinner()

      // Read the file directly by key and use duck-typing so we don't depend on
      // instanceof File (which can silently fail when the object comes from a
      // different JS realm or after JSON serialisation round-trips in PsFilters).
      const isFileLike = (v) => v !== null && v !== undefined && typeof v === 'object' && typeof v.size === 'number' && typeof v.slice === 'function'
      let uploadFile = null
      if (this.chunkedUpload && isFileLike(this.filterValues.file)) {
        uploadFile = this.filterValues.file
      }

      const formData = new FormData()
      Object.entries(this.filterValues).forEach(([key, value]) => {
        if (uploadFile && key === 'file') {
          // held back — will either go through importFileChunked or be appended below
        } else if (Array.isArray(value) && value.every(v => v instanceof File)) {
          value.forEach(file => formData.append(`${key}[]`, file))
        } else {
          formData.append(key, value)
        }
      })

      const CHUNKED_THRESHOLD = 90 * 1024 * 1024 // 90 MB — safely under Cloudflare's 100 MB limit
      let uploadPromise = null

      if (uploadFile && uploadFile.size > CHUNKED_THRESHOLD) {
        uploadPromise = this.importFileChunked(uploadFile, formData)
      } else {
        if (uploadFile) formData.append('file', uploadFile)
        const importURL = this.importUrl || `${this.url}/import`
        uploadPromise = this.httpClientToUse.post(importURL, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      }

      uploadPromise
        .then(response => {
          this.hideSpinnerAndLoading()
          this.$emit('beforeShowingData', { filterValues: this.filterValues })
          this.filterValues.file = null
          this.search()

          const count =
            response?.data?.data?.rows_inserted ??
            response?.data?.data?.rows_updated ??
            (Array.isArray(response?.data) ? response.data.length : 0)

          if (this.importSuccessMessage) {
            this.alertSuccess(this.importSuccessMessage)
          } else {
            this.alertSuccess(count ? `${count} data has been imported` : 'Data has been imported')
          }
          this.isBusy = false
        })
        .catch(this.handleErrors)
        .finally(() => {
          this.filterValues.file = null
        })
    },

    async importFileChunked (file, metaFormData) {
      const CHUNK_SIZE = 50 * 1024 * 1024 // 50 MB per part
      const totalParts = Math.ceil(file.size / CHUNK_SIZE)
      const baseUrl = this.importUrl || `${this.url}/import`

      // Collect scalar filter values that were already added to formData
      const meta = {}
      metaFormData.forEach((value, key) => { meta[key] = value })

      // Step 1 — initiate the multipart upload and receive upload_id + target path
      const { data: { upload_id: uploadId, r2_path: r2Path } } = await this.httpClientToUse.post(
        `${baseUrl}/chunk/initiate`,
        { ...meta, filename: file.name }
      )

      try {
        const parts = []

        // Step 2 — upload each 50 MB slice sequentially
        for (let i = 0; i < totalParts; i++) {
          const start = i * CHUNK_SIZE
          const chunkBlob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size))
          const partData = new FormData()
          partData.append('upload_id', uploadId)
          partData.append('r2_path', r2Path)
          partData.append('part_number', String(i + 1))
          partData.append('chunk', chunkBlob, file.name)

          const { data } = await this.httpClientToUse.post(
            `${baseUrl}/chunk/upload-part`,
            partData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
          parts.push({ part_number: i + 1, etag: data.etag })
        }

        // Step 3 — assemble and trigger import
        return this.httpClientToUse.post(`${baseUrl}/chunk/complete`, {
          ...meta,
          upload_id: uploadId,
          r2_path: r2Path,
          parts,
          file_size: file.size,
          original_filename: file.name,
        })
      } catch (error) {
        // Best-effort abort to release incomplete parts from R2
        this.httpClientToUse
          .post(`${baseUrl}/chunk/abort`, { upload_id: uploadId, r2_path: r2Path })
          .catch(() => {})
        throw error
      }
    },
    handleResponse (response) {
      this.hideSpinnerAndLoading()
      this.onlyGetFilterHasApplied = true
      if (response.data?.data) {
        this.hasSearchedData = true
      }
      this.$emit('beforeShowingData', { filterValues: this.filterValues })
      const responseData = response.data
      this.$emit('prepareResponseData', responseData, { filterValues: this.filterValues })

      // Determine items list
      let itemsList = []
      if (responseData.data) {
          itemsList = (responseData.data &&
              typeof responseData.data === 'object' &&
              !Array.isArray(responseData.data)) ? Object.values(responseData.data) : responseData.data
      }

      // Logic for dynamic pagination switch
      if (this.useEnhancePagination) {
         // Check total items vs itemPerPage
         // If total items <= items per page (or just check items length vs meta/limit, but requirements say total < itemPerPage)
         // Wait, usually if total <= per page, frontend pagination is fine because we have all data.
         // Actually, if backend pagination is active, we only get 'perPage' items.
         // If we switch to frontend pagination, we need ALL items.
         // But if total items for the ENTIRE set is small enough?
         // User said: "if total < itemPerPage we switch to the frontendPagination"
         // This implies if the TOTAL dataset size is small enough to fit in one page?
         // OR, maybe it means if the returned data count is small?
         
         // Assuming logic: If the dataset returned is the FULL dataset (or small enough), treat as frontend pagination.
         // Usually 'total' comes from meta.
         
         let total = 0
         if (this.customKeyDataPagination && responseData[this.customKeyDataPagination]) {
             total = responseData[this.customKeyDataPagination].total
         } else if (responseData.meta) {
             total = responseData.meta.total
         } else if (Array.isArray(itemsList)) {
             total = itemsList.length
         }
         
         // If total is explicit and <= itemsPerPage, we can assume we have all items (if backend respects it).
         // BUT if backend paginates, we only have one page.
         // If total < itemPerPage, then we certainly have all items in this response (since it fits on page 1).
         // So it is safe to switch to frontend pagination for sorting/filtering on client side.
         
         if (total <= this.params.itemPerPage) {
             this.dynamicIsFrontendPagination = true
         } else {
             this.dynamicIsFrontendPagination = this.isFrontendPagination
         }
      } else {
          this.dynamicIsFrontendPagination = this.isFrontendPagination
      }

      if (this.dynamicIsFrontendPagination) {
         this.allItems = itemsList
         // Reset items for safety or keep them?
         // displayedItems uses sortedItems which uses allItems if dynamicIsFrontendPagination is true.
      } else {
         this.items = itemsList
      }

      this.responseData = responseData
      this.initiateFilters(responseData)
      this.$emit('updateStatistic', responseData.statistic_data)
      this.$emit('afterShowingData', responseData)
      this.isBusy = false
    },
    handleErrors (errors) {
      if (errors?.code === 'ERR_CANCELED') {
        // Keep loader state; a new request likely started immediately after cancel
        return
      }
      const errorMsg =
          errors.response && errors.response.data ? (errors.response.data.errors || errors.response.data) : errors.message
      this.alertError(errorMsg)
      this.hideSpinnerAndLoading()
      this.isBusy = false
    },
    handleChangePage (page) {
      this.params.page = page
      this.sendActionLog(`change pagination ${this.$route.path} with params ${JSON.stringify(this.params)}`)

      if (this.isFrontendPagination) {
        if (this.showPagination) {
          this.items = this.allItems.slice(this.paginationData.from, this.paginationData.to)
        } else {
          this.items = this.allItems
        }
      } else {
        this.search()
      }
    },
    addDynamicFields () {
      const newFields = Object.entries(this.dynamicFields).map(([key, value]) => {
        const newNum = this.filterSettings[this.indexDynamicField].length
        const newKeyName = key.replace(/\d+/, newNum)
        const items = Object.assign({}, value)

        items.defaultValue = ''
        items.data = []

        if (value.customGenerateSuggestion) {
          items.customGenerateSuggestion = value.customGenerateSuggestion
        }

        if (value.change) {
          items.change = value.change
        }

        return [newKeyName, items]
      })

      this.filterSettings[this.indexDynamicField] = [
        ...this.filterSettings[this.indexDynamicField],
        Object.fromEntries(newFields)
      ]

      this.$forceUpdate()
    },
    async exportToCSV () {
      this.isBusy = true
      if (this.showSpinner || this.useSpinnerLoading) this.showLoading()
      if (this.useSpinnerLoading) this.showLoadingSpinner()

      // Check if the method is 'post', and structure the request accordingly
      // eslint-disable-next-line multiline-ternary
      const requestData = this.method === 'post'
      // eslint-disable-next-line multiline-ternary
        ? {
          // POST request sends the data directly (without 'body' or 'params' keys)
          ...this.params,
          ...this.filterValues,
          only_file_name: true
        }
        : {
          // GET request sends the data as URL parameters
          params: {
            ...this.params,
            ...this.filterValues,
            only_file_name: true
          }
        }

      if (this.exportUrl) {
        const response = await this.httpClientToUse[this.method](this.exportUrl, {
          ...this.filterValues,
          responseType: 'blob'
        })

        if (this.showSpinner || this.useSpinnerLoading) this.hideLoading()
        if (this.useSpinnerLoading) this.hideLoadingSpinner()

        // Create a new Blob object using the response data
        const blob = new Blob([response.data], { type: 'text/csv' })

        // Create a URL for the blob object
        const downloadUrl = window.URL.createObjectURL(blob)

        // Create a temporary <a> element to trigger the download
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', `${this.exportName}.csv`) // Set the file name

        // Append the link to the document and trigger the click event
        document.body.appendChild(link)
        link.click()

        // Clean up the URL and remove the link element
        link.remove()
        window.URL.revokeObjectURL(downloadUrl)

        this.isBusy = false
      } else {
        this.httpClientToUse[this.method](`${this.url}/export`, this.method === 'get' ? { ...requestData } : { ...requestData })
          .then((response) => {
            const filename = response.data
            const dynamicFieldCount = Math.max((this.filterSettings?.[this.indexDynamicField]?.length ?? 0) - 1, 0)

            // For the second request to fetch the file
            // eslint-disable-next-line multiline-ternary
            const secondRequestData = this.method === 'post'
            // eslint-disable-next-line multiline-ternary
              ? {
                // POST request sends the data directly
                ...this.params,
                ...this.filterValues,
                fields: this.flattenedFields,
                dynamicFieldCount
              }
              : {
                // GET request sends the data as URL parameters
                params: {
                  ...this.params,
                  ...this.filterValues,
                  fields: this.flattenedFields,
                  dynamicFieldCount
                }
              }

            const secondConfig = this.method === 'get' ? { ...secondRequestData, responseType: 'blob' } : { responseType: 'blob' }
            const secondPayload = this.method === 'get' ? secondConfig : { ...secondRequestData }
            this.httpClientToUse[this.method](`${this.url}/export`, secondPayload, secondConfig)
              .then((response) => {
                if (this.showSpinner || this.useSpinnerLoading) this.hideLoading()
                if (this.useSpinnerLoading) this.hideLoadingSpinner()

                // Create a downloadable link for the exported file
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', filename) // Filename from the first response
                document.body.appendChild(link)
                link.click() // Trigger download

                this.isBusy = false
              })
              .catch((errors) => {
                this.handleExportErrors(errors)
              })
          })
          .catch((errors) => {
            this.handleExportErrors(errors)
          })
      }
    },
    hideSpinnerAndLoading () {
      if (this.showSpinner) this.hideLoading()
      if (this.useSpinnerLoading) this.hideLoadingSpinner()
    },
    initiateFilters (responseData) {
      if (this.params.initFilter === 0 && responseData.filterData) {
        const filterSettingsArray = Array.isArray(this.filterSettings) ? this.filterSettings : [this.filterSettings]
        Object.keys(responseData.filterData).forEach(property => {
          filterSettingsArray.forEach(setting => {
            if (setting[property]) {
              if (
                Array.isArray(setting[property].data) &&
                  setting[property].defaultData !== undefined
              ) {
                setting[property].data = [setting[property].defaultData, ...responseData.filterData[property]]
              } else {
                setting[property].data = responseData.filterData[property]
              }
              const { defaultValue = null } = setting[property] || {}
              if (Array.isArray(defaultValue) && defaultValue.includes('all')) {
                setting[property].defaultValue = responseData.filterData[property].map(item => item.value)
              }
            }
          })
        })
        this.params.initFilter = 1
        if (this.$refs.filterBar.$refs.filter && !responseData.filterData.isAdditionalFilter) {
          this.$refs.filterBar.$refs.filter.generateModels?.()
        }
      }
    },
    setSortField (fieldName) {
      this.params.orderBy = fieldName
    },
    handleExportErrors (errors) {
      if (errors?.code === 'ERR_CANCELED') {
        // Do not modify loader state on cancel; follow-up requests may be in-flight
        return
      }
      if (typeof errors.response === 'object') {
        if (typeof errors.response.data.errors === 'object') {
          this.alertError(errors.response.data.errors)
        } else {
          this.alertError(errors.response.data)
        }
      } else {
        this.alertError(errors.message)
      }

      if (this.showSpinner || this.useSpinnerLoading) this.hideLoading()
      if (this.useSpinnerLoading) this.hideLoadingSpinner()
      this.isBusy = false
    }
  },
  created () {
    Object.keys(this.filterSettings).forEach(property => {
      if (
        Array.isArray(this.filterSettings[property].data) &&
          this.filterSettings[property].defaultData !== undefined
      ) {
        this.filterSettings[property].data.push(this.filterSettings[property].defaultData)
      }
    })
    if (!Object.keys(this.filterSettings).length) this.search()
  },
  beforeDestroy () {
    try { this.abortController && this.abortController.abort() } catch (e) {}
  }
}
</script>

<style lang="scss">

[dir] .table-striped tbody tr:not(:hover):nth-of-type(even) .position-sticky:not(:hover) {
  background-color: white;
  z-index: 2;
}

[dir] .table-striped tbody tr:not(:hover):nth-of-type(odd) .position-sticky:not(:hover) {
  background-color: #fafafc;
  z-index: 2;
}

[dir] .table-striped tbody tr:hover .position-sticky {
  background: inherit;
}

.table-column-sticky {
  .table-responsive {
    overflow-y: hidden;

    table {
      overflow-y: hidden;
    }
  }

  td {
    position: relative;
  }

  tr:not(td.position-sticky):hover {
    background-color: #e6e6e6 !important;

    td:not(td.position-sticky) {
      z-index: 1;
    }

    td.position-sticky {
      background-color: #e6e6e6 !important;
      z-index: 2;
      width: 100%;
    }
  }

  td:not(.position-sticky):hover {
    &:not(.is-gradient-color)::after {
      background-color: rgb(226 226 224 / 30%);
      content: '\00a0';
      height: 200vh;
      left: 0;
      position: absolute;
      top: -100vh;
      width: 100%;
      pointer-events: none;
    }
  }
}

/* Dark Mode Styles */
.dark-layout .table-column-sticky tbody tr:not(:hover):nth-of-type(even) .position-sticky:not(:hover) {
  background-color: rgb(36, 43, 61);
  z-index: 2;
}

.dark-layout .table-column-sticky tbody tr:not(:hover):nth-of-type(odd) .position-sticky:not(:hover) {
  background-color: #242b3d;
  z-index: 2;
}

.dark-layout .table-column-sticky tr:not(td.position-sticky):hover {
  background-color: #011528 !important;

  td:not(td.position-sticky) {
    z-index: 1;
  }

  td.position-sticky {
    background-color: #011528 !important;
    z-index: 2;
    width: 100%;
  }
}

.dark-layout .table-column-sticky td:not(.position-sticky):hover:not(.is-gradient-color)::after {
  background-color: rgba(0, 0, 0, 0.33);
  content: '\00a0';
  height: 200vh;
  left: 0;
  position: absolute;
  top: -100vh;
  width: 100%;
  pointer-events: none;
}

/* General table-hover overrides */
/* Light Mode */
.table-hover tbody tr:hover {
  background-color: #e6e6e6 !important;
}
/* Dark Mode */
.dark-layout .table-hover tbody tr:hover {
  background-color: #011528 !important;
}


.base-table-header .card-header {
  padding-bottom: 0.5rem !important;
}
thead tr.border th {
  border: 1px solid #dae1e7 !important;
}

.right-divider {
  box-shadow: inset -3px 0 1px -1px #000;
}
.left-divider {
  box-shadow: inset 3px 0 1px -1px #000;
}

/* In-table overlay */
.base-table-wrapper { position: relative; }
.table-loading-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.6); z-index: 10;
}
.disabled-overlay { pointer-events: none; opacity: 0.6; }

</style>

